/**
 * RecordingFeature — Top-level container that assembles every recording
 * sub-component and drives them via the useRecordingState hook.
 *
 * IMPORTANT stacking-context note:
 * The header pill lives inside `.ideaweave-canvas__shell` which has
 * `will-change: transform`.  That creates a new containing block so
 * any `position: fixed; z-index` inside it cannot compete with
 * elements portalled to <body>.
 *
 * Strategy:
 *   • idle / expanded  → buttons render inside the header pill (normal)
 *   • frameSelect / recording / finished → buttons are portalled to
 *     <body> inside their own bright pill container (z-index > overlay)
 *   • frame overlay, avatar bubble, teleprompter, settings are always
 *     portalled to <body> for correct global z-index layering.
 *
 * Video generation (one-click AI video) is managed externally via
 * useOneClickVideo and rendered through AIVideoButton in the header.
 * RecordingFeature still renders the VideoSettings modal and toast
 * portalled to <body>.
 */

import React, { useEffect } from "react";
import { createPortal } from "react-dom";

import { useAtom } from "../app-jotai";
import { useAppI18n } from "../hooks/useAppI18n";
import { pendingVideoBlobAtom } from "../hooks/useAIVideoSignal";
import { useRecordingState, type VideoGenSettings } from "../hooks/useRecordingState";

import { RecordingButton } from "./RecordingButton";
import { RecordingTeleprompter } from "./RecordingTeleprompter";
import { RecordingSettings } from "./RecordingSettings";
import { VideoSettings } from "./VideoSettings";
import { RecordingFrame } from "./RecordingFrame";
import { AvatarBubble } from "./AvatarBubble";
import "./RecordingFeature.scss";

export interface RecordingFeatureProps {
  /** Video generation state from useOneClickVideo, rendered as portal */
  video?: {
    isVideoSettingsOpen: boolean;
    closeVideoSettings: () => void;
    startGeneration: (settings: VideoGenSettings) => void;
    voiceSampleUrlBuilder: ((voice: string) => string) | undefined;
    showToast: boolean;
  };
}

export const RecordingFeature: React.FC<RecordingFeatureProps> = ({
  video,
}) => {
  const { t } = useAppI18n();
  const state = useRecordingState();
  const [pendingVideoBlob, setPendingVideoBlob] = useAtom(pendingVideoBlobAtom);

  useEffect(() => {
    if (pendingVideoBlob) {
      state.setFinishedVideo(pendingVideoBlob);
      setPendingVideoBlob(null);
    }
  }, [pendingVideoBlob, state, setPendingVideoBlob]);

  const isFrameSelect = state.phase === "frameSelect";
  const isRecording = state.phase === "recording";
  const isFinished = state.phase === "finished";
  const showBubble =
    (isFrameSelect || isRecording) && state.settings.showCamera;

  const needsPortalBar = isFrameSelect || isRecording || isFinished;

  const buttonBar = (
    <RecordingButton
      phase={state.phase}
      elapsedSeconds={state.elapsedSeconds}
      isPaused={state.isPaused}
      onExpand={state.expand}
      onCollapse={state.collapse}
      onRecord={state.enterFrameSelect}
      onOpenSettings={state.openSettings}
      onToggleTeleprompter={state.toggleTeleprompter}
      isTeleprompterOpen={state.isTeleprompterOpen}
      onCancel={state.cancelFrameSelect}
      onStartRecording={state.startRecording}
      onPause={state.pauseRecording}
      onResume={state.resumeRecording}
      onStop={state.stopRecording}
      onDownload={state.download}
      onDiscard={state.discard}
      isConverting={state.isConverting}
    />
  );

  return (
    <>
      {!needsPortalBar && buttonBar}

      {needsPortalBar && (
        <div
          className="recording-btn-group"
          style={{
            position: "absolute",
            width: 0,
            height: 0,
            overflow: "hidden",
          }}
          aria-hidden="true"
        />
      )}

      {createPortal(
        <>
          {needsPortalBar && (
            <div className="recording-portal-wrap">
              <div className="recording-portal-pill">{buttonBar}</div>
            </div>
          )}

          {video?.showToast && (
            <div className="recording-gen-toast">
              <span className="material-symbols-outlined" aria-hidden>
                videocam
              </span>
              <span>{t("aiVideo.toastStarted")}</span>
            </div>
          )}

          <RecordingFrame
            phase={state.phase}
            frame={state.frame}
            cornerRadius={state.settings.cornerRadius}
            aspectRatio={state.settings.aspectRatio}
            onUpdateFrame={state.updateFrame}
          />

          <AvatarBubble
            visible={showBubble}
            size={state.settings.cameraSize}
            frameRect={state.frame}
          />

          <RecordingTeleprompter
            isOpen={state.isTeleprompterOpen}
            script={state.teleprompterScript}
            speed={state.teleprompterSpeed}
            opacity={state.teleprompterOpacity}
            isPlaying={state.isTeleprompterPlaying}
            onClose={state.closeTeleprompter}
            onScriptChange={state.setTeleprompterScript}
            onSpeedChange={state.setTeleprompterSpeed}
            onOpacityChange={state.setTeleprompterOpacity}
            onTogglePlayback={state.toggleTeleprompterPlayback}
          />

          <RecordingSettings
            isOpen={state.isSettingsOpen}
            settings={state.settings}
            onUpdate={state.updateSettings}
            onClose={state.closeSettings}
          />

          {video && (
            <VideoSettings
              isOpen={video.isVideoSettingsOpen}
              onGenerate={video.startGeneration}
              onClose={video.closeVideoSettings}
              voiceSampleUrlBuilder={video.voiceSampleUrlBuilder}
            />
          )}
        </>,
        document.body,
      )}
    </>
  );
};
